import type {
  DesignAssetView,
  DesignBody,
  FaceKey,
  ResolvedEditAreaDefinition,
} from '@repo/api-contracts';
import {
  type CreateContext,
  type FacePreviewResult,
  type MeasureText,
  type RenderMaterials,
  renderFacePreview,
} from '@repo/design-renderer';
import { createRasterCache } from '@repo/design-viewer';
import type { Raster } from '@repo/warp-engine';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FontFaces } from '@/entities/design';
import {
  createCanvasContext,
  createCanvasMeasure,
  ImageLoadError,
  loadImageRaster,
} from '@/shared/lib';
import { collectAssetIds, collectFontIds } from './document-ops';
import type { EditorMode } from './editor-transitions';

export interface FacePreviewDeps {
  readonly loadRaster: (url: string) => Promise<Raster>;
  readonly render: typeof renderFacePreview;
  readonly createContext: CreateContext;
  readonly measure: MeasureText;
}

export interface UseFacePreviewInput {
  readonly definition: ResolvedEditAreaDefinition | null;
  readonly mode: EditorMode;
  readonly revision: number;
  readonly faceKey: FaceKey;
  readonly colorOptionId: string;
  readonly body: DesignBody;
  readonly assetUrls: ReadonlyMap<string, DesignAssetView>;
  /** 에셋 표시 URL 조회 중인지. 본문 이미지 URL 이 비어 있고 조회 중이면 미리보기를 기다린다. */
  readonly assetUrlsPending: boolean;
  readonly fonts: FontFaces;
  readonly inkColors: readonly { readonly id: string; readonly colorCode: string }[];
  readonly fontIds: readonly string[];
  readonly refreshAssetUrls: () => Promise<void>;
  readonly refreshEditArea: () => Promise<void>;
  readonly onSettled: (revision: number, result: FacePreviewResult) => void;
  readonly onFailed: (revision: number, reason: string) => void;
  readonly deps?: Partial<FacePreviewDeps>;
}

export interface FacePreviewState {
  readonly result: FacePreviewResult | null;
  readonly renderMs: number | null;
}

const describeError = (error: unknown): string =>
  error instanceof Error ? error.message : '미리보기를 만들 수 없습니다.';

/**
 * 선택 해제 시 면 미리보기를 만든다(T8). 재료(사진·마스크·음영·고객 이미지·폰트)를 모아 renderFacePreview 를 부르고,
 * 만료(403)는 URL 을 한 번 다시 받아 재시도한다. revision 이 바뀌면 늦은 결과를 버린다. 이전 결과는 다음 결과까지 유지한다.
 * 효과는 mode·revision·definition·본문 폰트·이미지 URL 준비 상태가 바뀔 때만 다시 돌고, 나머지 입력은 latest ref 로 읽는다.
 */
export const useFacePreview = (input: UseFacePreviewInput): FacePreviewState => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: 대역은 마운트 시 한 번만 고정한다.
  const deps = useMemo<FacePreviewDeps>(
    () => ({
      loadRaster: input.deps?.loadRaster ?? loadImageRaster,
      render: input.deps?.render ?? renderFacePreview,
      createContext: input.deps?.createContext ?? createCanvasContext,
      measure: input.deps?.measure ?? createCanvasMeasure(),
    }),
    [],
  );
  const cache = useMemo(() => createRasterCache(deps.loadRaster), [deps]);
  const [state, setState] = useState<FacePreviewState>({ result: null, renderMs: null });
  const latest = useRef(input);
  latest.current = input;
  // 이 면 본문이 쓰는 폰트만 기다린다 — 관계없는 폰트의 로드가 미리보기를 막지 않는다.
  const fontsSettled = input.fonts.settledFor(collectFontIds(input.body));
  // 본문 이미지의 표시 URL 이 아직 오지 않았으면 기다렸다가 도착하면 다시 그린다. 조회가 끝났는데 없는 에셋은 빼고 그린다.
  const assetsReady =
    !input.assetUrlsPending ||
    collectAssetIds(input.body).every((assetId) => input.assetUrls.has(assetId));

  // biome-ignore lint/correctness/useExhaustiveDependencies: 나머지 입력은 latest ref 로 읽어 재렌더 루프를 막는다.
  useEffect(() => {
    const start = latest.current;
    const { definition } = start;
    if (start.mode !== 'rendering' || definition === null || !fontsSettled || !assetsReady) return;
    const { revision, faceKey, colorOptionId, body } = start;
    const isCurrent = (): boolean =>
      latest.current.revision === revision && latest.current.mode === 'rendering';
    const run = async (): Promise<void> => {
      const color = definition.colors.find((entry) => entry.colorOptionId === colorOptionId);
      const facePhoto = color?.faces[faceKey];
      const composite = definition.faces[faceKey]?.composite;
      if (facePhoto === undefined) throw new RangeError('이 색상의 면 사진이 없습니다.');
      const loadWithRetry = async (
        key: string,
        url: string,
        refresh: () => Promise<void>,
        freshUrlOf: (current: UseFacePreviewInput) => string | undefined,
      ): Promise<Raster> => {
        try {
          return await cache.get(key, url);
        } catch (error) {
          if (!(error instanceof ImageLoadError) || error.kind !== 'expired') throw error;
          await refresh();
          // refetch 프라미스는 구독자 재렌더가 커밋되기 전에 끝날 수 있다. latest 가 새 URL 을 담도록 매크로태스크 한 번을 양보한다.
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
          cache.invalidate(key);
          return cache.get(key, freshUrlOf(latest.current) ?? url);
        }
      };
      const editAreaUrl =
        (pick: (fresh: ResolvedEditAreaDefinition) => string | undefined) =>
        (current: UseFacePreviewInput): string | undefined =>
          current.definition === null ? undefined : pick(current.definition);
      const refreshEditArea = (): Promise<void> => latest.current.refreshEditArea();
      const refreshAssetUrls = (): Promise<void> => latest.current.refreshAssetUrls();
      const started = performance.now();
      const [photo, mask, shading] = await Promise.all([
        loadWithRetry(
          facePhoto.photoKey,
          facePhoto.photoUrl,
          refreshEditArea,
          editAreaUrl(
            (fresh) =>
              fresh.colors.find((entry) => entry.colorOptionId === colorOptionId)?.faces[faceKey]
                ?.photoUrl,
          ),
        ),
        composite?.maskKey === undefined || composite.maskUrl === undefined
          ? undefined
          : loadWithRetry(
              composite.maskKey,
              composite.maskUrl,
              refreshEditArea,
              editAreaUrl((fresh) => fresh.faces[faceKey]?.composite?.maskUrl),
            ),
        composite?.shadingKey === undefined || composite.shadingUrl === undefined
          ? undefined
          : loadWithRetry(
              composite.shadingKey,
              composite.shadingUrl,
              refreshEditArea,
              editAreaUrl((fresh) => fresh.faces[faceKey]?.composite?.shadingUrl),
            ),
      ]);
      const images = new Map<string, Raster>();
      for (const assetId of collectAssetIds(body)) {
        const url = latest.current.assetUrls.get(assetId)?.url;
        if (url === undefined) continue;
        images.set(
          assetId,
          await loadWithRetry(
            assetId,
            url,
            refreshAssetUrls,
            (current) => current.assetUrls.get(assetId)?.url,
          ),
        );
      }
      const { fonts, fontIds, inkColors } = latest.current;
      const materials: RenderMaterials = {
        images,
        fonts: new Map(
          fontIds.flatMap((id) => {
            const family = fonts.familyOf(id);
            return family === undefined ? [] : [[id, { familyName: family }] as const];
          }),
        ),
        inks: new Map(inkColors.map((ink) => [ink.id, ink.colorCode])),
      };
      if (!isCurrent()) return;
      const result = deps.render({
        definition,
        colorOptionId,
        faceKey,
        body,
        materials,
        face: {
          photo,
          ...(mask === undefined ? {} : { mask }),
          ...(shading === undefined ? {} : { shading }),
        },
        createContext: deps.createContext,
        measure: deps.measure,
      });
      if (!isCurrent()) return;
      setState({ result, renderMs: performance.now() - started });
      latest.current.onSettled(revision, result);
    };
    run().catch((error: unknown) => {
      if (isCurrent()) latest.current.onFailed(revision, describeError(error));
    });
  }, [input.mode, input.revision, input.definition, fontsSettled, assetsReady, cache, deps]);

  return state;
};
